import { segmentShares, toneSegments } from './scale';
import { formatNumber } from '@/shared/lib/format';
import { TONE_COLORS } from '@/shared/lib/tones';
import { cn } from '@/shared/lib/utils';
import type { StatusCounts } from '@/shared/types/domain';

interface StatusBarProps {
  counts: StatusCounts;
  /**
   * De quem são estes números, por extenso — "no seu alcance", "em Louvor".
   *
   * **Obrigatório, e é a decisão de desenho mais importante deste componente.**
   * Toda contagem do painel chega recortada pela RLS, e uma barra sem moldura
   * afirma sobre a igreja o que só vale para quem está olhando. Foi o defeito
   * que tirou o panorama consolidado da tela de coordenação (PRD US-11); aqui
   * ele é impossível de repetir por esquecimento, porque o tipo exige a frase.
   */
  scopeLabel: string;
  className?: string;
}

/**
 * O acervo inteiro numa barra: quanto há, e em que pé está.
 *
 * **Substitui os cinco `StatusTiles` no topo da Início**, e a troca tem
 * argumento: cinco caixas com um número cada respondem "quantos há de cada",
 * mas escondem a proporção — que é a pergunta que alguém faz ao entrar. Trinta
 * projetos com vinte concluídos é um escopo saudável; trinta com vinte parados
 * em análise é um problema, e nos tiles os dois se parecem. Numa barra, não.
 *
 * `StatusTiles` continua existindo e continua certo onde está: na tela de um
 * ministério, o número exato de cada situação é o assunto.
 *
 * ⚠️ **Cor nunca é o único portador** (US-14): cada segmento tem número e
 * rótulo por extenso na legenda logo abaixo, e a barra em si é `aria-hidden`.
 * Quem não distingue ciano de verde lê a mesma coisa na legenda.
 */
export function StatusBar({ counts, scopeLabel, className }: StatusBarProps) {
  const segments = toneSegments(counts);
  const widths = segmentShares(segments);
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  if (total === 0) {
    return (
      <p className={cn('text-sm text-muted-foreground', className)}>
        Nenhum projeto {scopeLabel}.
      </p>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      <p className="text-sm text-muted-foreground">
        <span className="tabular font-semibold text-foreground">{formatNumber(total)}</span>{' '}
        {total === 1 ? 'projeto' : 'projetos'} {scopeLabel}
      </p>

      {/* A barra é decorativa: tudo que ela diz está escrito na legenda abaixo. */}
      <div
        aria-hidden="true"
        className="flex h-3 w-full overflow-hidden rounded-full bg-muted"
      >
        {segments.map((segment, index) => (
          <div
            key={segment.key}
            className={cn('h-full', TONE_COLORS[segment.tone].solid)}
            style={{ width: `${widths[index]}%` }}
          />
        ))}
      </div>

      <dl className="flex flex-wrap gap-x-4 gap-y-1.5">
        {segments.map((segment) => (
          <div key={segment.key} className="flex items-center gap-1.5 text-sm">
            <span
              aria-hidden="true"
              className={cn('size-2 shrink-0 rounded-full', TONE_COLORS[segment.tone].solid)}
            />
            <dt className="text-muted-foreground">{segment.label}</dt>
            <dd className="tabular font-medium">{formatNumber(segment.value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
